import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { TransferTargetAudit, TransferTargetKunde } from '../../types';
import { schwachstellenService } from '../../services/schwachstellenService';

interface SchwachstelleTransferDialogProps {
  open: boolean;
  isDark: boolean;
  schwachstelleId: number | null;
  schwachstelleTitel: string;
  excludeAuditId: number | null;
  currentKundeId: number | null;
  onClose: () => void;
  onTransferred: (zielAuditName: string) => void;
}

const SchwachstelleTransferDialog: React.FC<SchwachstelleTransferDialogProps> = ({
  open,
  isDark,
  schwachstelleId,
  schwachstelleTitel,
  excludeAuditId,
  currentKundeId,
  onClose,
  onTransferred,
}) => {
  const [kunden, setKunden] = useState<TransferTargetKunde[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKunde, setSelectedKunde] = useState<TransferTargetKunde | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<TransferTargetAudit | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSelectedKunde(null);
    setSelectedAudit(null);

    schwachstellenService
      .transferTargets(excludeAuditId ?? undefined)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setKunden(data);
        const preselected = data.find((kunde) => kunde.id === currentKundeId) ?? null;
        setSelectedKunde(preselected);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Ziele konnten nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, excludeAuditId, currentKundeId]);

  const auditOptions = useMemo(() => selectedKunde?.audits ?? [], [selectedKunde]);

  const handleConfirm = async () => {
    if (!schwachstelleId || !selectedAudit) {
      return;
    }
    setSaving(true);
    try {
      await schwachstellenService.transfer(schwachstelleId, selectedAudit.id);
      onTransferred(selectedAudit.name);
    } catch {
      // Fehler werden zentral behandelt
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>
        In anderes Audit verschieben
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {schwachstelleTitel
            ? `Schwachstelle „${schwachstelleTitel}“ in ein anderes Audit verschieben.`
            : 'Schwachstelle in ein anderes Audit verschieben.'}
        </Typography>
        {loading ? (
          <CircularProgress size={28} />
        ) : loadError ? (
          <Typography color="error">{loadError}</Typography>
        ) : kunden.length === 0 ? (
          <Typography color="text.secondary">Keine anderen Audits verfügbar.</Typography>
        ) : (
          <>
            <Autocomplete
              options={kunden}
              value={selectedKunde}
              onChange={(_event, next) => {
                setSelectedKunde(next);
                setSelectedAudit(null);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Kunde" size="small" />}
              sx={{ mb: 2, mt: 0.5 }}
            />
            <Autocomplete
              options={auditOptions}
              value={selectedAudit}
              onChange={(_event, next) => setSelectedAudit(next)}
              getOptionLabel={(option) =>
                option.projekt_name ? `${option.name} (${option.projekt_name})` : option.name
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedKunde}
              noOptionsText="Keine Audits für diesen Kunden"
              renderInput={(params) => <TextField {...params} label="Audit" size="small" />}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: isDark ? '#2c3358' : '#eef1f5' } }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          variant="contained"
          disabled={!selectedAudit || saving}
        >
          {saving ? 'Verschieben…' : 'Verschieben'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchwachstelleTransferDialog;
